import { Component, inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core';
import { EntityLineRevisionChangeService } from '../../services/entity-line-revision-change.service';
import { EntityLineRevisionChange } from '../../model/entity-line-revision-change';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StringUtils } from '../../utils/string-utils';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-entity-revision-changes-dialog',
  imports: [FormsModule, MatButtonModule],
  templateUrl: './entity-revision-changes-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './entity-revision-changes-dialog.component.css',
})
export class EntityRevisionChangesDialogComponent implements OnInit {
  private readonly changesService = inject(EntityLineRevisionChangeService);
  private readonly dialogRef = inject<MatDialogRef<EntityRevisionChangesDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly entityId = signal<string | undefined>(undefined!);
  private readonly currentRevision = signal(0);
  private readonly noChanges = 'No changes made';
  readonly changes = signal<EntityLineRevisionChange[]>([]);
  otherChanges: string | undefined;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);

  ngOnInit(): void {
    this.isBusy.set(true);
    this.changesService.getEntityUpdates(this.data.entityId).subscribe({
      next: (changes: EntityLineRevisionChange[]) => {
        this.changes.set(changes);
        this.isBusy.set(false);
        if ((this.changes()?.length || 0) === 0) {
          this.dialogRef.close();
        } else {
          this.entityId.set(this.changes()[0].entityId);
          this.currentRevision.set(this.changes()[0].currentRevisionId);
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    if (this.otherChanges && StringUtils.isNotBlank(this.otherChanges)) {
      const otherChange: EntityLineRevisionChange = new EntityLineRevisionChange();
      otherChange.changeDescription = this.otherChanges;
      otherChange.entityId = this.entityId() || '';
      otherChange.lineId = '0';
      otherChange.currentRevisionId = this.currentRevision();
      otherChange.previousRevisionId = this.currentRevision() - 1;
      this.changes.update(current => [...current, otherChange]);
    }
    this.errorMessage.set(this.validateEntries());
    this.isBusy.set(false);
    if (StringUtils.isBlank(this.errorMessage())) {
      this.dialogRef.close(this.changes());
    }
  }

  private validateEntries(): string {
    let allNoChanges = true;
    for (const change of this.changes()) {
      if (StringUtils.isBlank(change.changeDescription)) {
        return "Please enter a description for each line.  Enter '" + this.noChanges + "' for lines that didn't change";
      } else if (!StringUtils.equalsIgnoreCase(this.noChanges, change.changeDescription)) {
        allNoChanges = false;
      }
    }
    if (allNoChanges) {
      return 'Please enter a value other than "' + this.noChanges + '" for at least one line';
    }
    return undefined!;
  }
}
